import { ThreeManager } from './three/index';
import { UIManager } from './ui/index';
import { InfoLogger } from './info-logger';
/**
 * Phoenix event display class for managing detector geometries and event data.
 */
var EventDisplay = /** @class */ (function () {
    /**
     * Constructor for the phoenix event display.
     */
    function EventDisplay() {
        /** Array containing callbacks to be called when events change. */
        this.onEventsChange = [];
        /** Array containing callbacks to be called when the displayed event changes. */
        this.onDisplayedEventChange = [];
        this.graphicsLibrary = new ThreeManager();
        this.infoLogger = new InfoLogger();
        this.ui = new UIManager(this.graphicsLibrary);
    }
    /**
     * Initializes the components needed to later represent the geometries.
     * @param configuration Configuration used to customize different aspects.
     */
    EventDisplay.prototype.init = function (configuration) {
        var _this = this;
        this.configuration = configuration;
        this.graphicsLibrary.init(configuration, this.infoLogger);
        // Showing the UI elements
        this.ui.showUI(configuration);
        if (this.frameID) {
            cancelAnimationFrame(this.frameID);
        }
        // Animate loop
        var animate = function () {
            _this.frameID = requestAnimationFrame(animate);
            _this.graphicsLibrary.updateControls();
            _this.ui.updateUI();
            _this.graphicsLibrary.render();
        };
        animate();
        // Allow adding elements through console
        this.enableEventDisplayConsole();
        // Allow keyboard controls
        this.enableKeyboardControls();
    };
    /**
     * Initialize VR.
     * @param onSessionEnded Callback when the VR session ends.
     */
    EventDisplay.prototype.initVR = function (onSessionEnded) {
        this.graphicsLibrary.initVRSession(onSessionEnded);
    };
    /**
     * End VR and remove VR settings.
     */
    EventDisplay.prototype.endVR = function () {
        this.graphicsLibrary.endVRSession();
    };
    /**
     * Receives an object containing all the eventKeys and saves it.
     * Then it loads by default the first event.
     * @param eventsData Object containing the event data.
     * @returns Array of strings containing the keys of the eventsData object.
     */
    EventDisplay.prototype.parsePhoenixEvents = function (eventsData) {
        this.eventsData = eventsData;
        var eventKeys = this.configuration.getEventDataLoader().getEventsList(eventsData);
        this.loadEvent(eventKeys[0]);
        this.onEventsChange.forEach(function (callback) { return callback(eventKeys); });
        return eventKeys;
    };
    /**
     * Receives an object containing one event and builds the different collections
     * of physics objects.
     * @param eventData Object containing the event data.
     */
    EventDisplay.prototype.buildEventDataFromJSON = function (eventData) {
        // Creating UI folder
        this.ui.addEventDataFolder();
        // Clearing existing event data
        this.graphicsLibrary.clearEventData();
        // Build data and add to scene
        this.configuration.getEventDataLoader().buildEventData(eventData, this.graphicsLibrary, this.ui, this.infoLogger);
        this.onDisplayedEventChange.forEach(function (callback) { return callback(eventData); });
    };
    /**
     * Receives a string representing the key of an event and loads
     * the event associated with that key.
     * @param eventKey String that represents the event in the eventsData object.
     */
    EventDisplay.prototype.loadEvent = function (eventKey) {
        var event = this.eventsData[eventKey];
        if (event) {
            this.buildEventDataFromJSON(event);
        }
    };
    /**
     * Get the three manager responsible for three.js functions.
     * @returns The three.js manager.
     */
    EventDisplay.prototype.getThreeManager = function () {
        return this.graphicsLibrary;
    };
    /**
     * Get the UI manager responsible for UI related functions.
     * @returns The UI manager.
     */
    EventDisplay.prototype.getUIManager = function () {
        return this.ui;
    };
    /**
     * Get the info logger containing event display logs.
     * @returns The info logger instance being used by the event display.
     */
    EventDisplay.prototype.getInfoLogger = function () {
        return this.infoLogger;
    };
    // **********************
    // * LOADING GEOMETRIES *
    // **********************
    /**
     * Loads an OBJ (.obj) geometry from the given filename
     * and adds it to the dat.GUI menu.
     * @param filename Path to the geometry.
     * @param name Name given to the geometry.
     * @param color Color to initialize the geometry.
     * @param doubleSided Renders both sides of the material.
     * @param initiallyVisible Whether the geometry is initially visible or not.
     */
    EventDisplay.prototype.loadOBJGeometry = function (filename, name, color, doubleSided, initiallyVisible) {
        if (initiallyVisible === void 0) { initiallyVisible = true; }
        this.graphicsLibrary.loadOBJGeometry(filename, name, color, doubleSided, initiallyVisible);
        this.ui.addGeometry(name, color, initiallyVisible);
        this.infoLogger.add(name, 'Loaded OBJ geometry');
    };
    /**
     * Parses and loads an OBJ geometry from the given content
     * and adds it to the dat.GUI menu.
     * @param content Content of the OBJ geometry.
     * @param name Name given to the geometry.
     * @param initiallyVisible Whether the geometry is initially visible or not.
     */
    EventDisplay.prototype.parseOBJGeometry = function (content, name, initiallyVisible) {
        if (initiallyVisible === void 0) { initiallyVisible = true; }
        this.graphicsLibrary.parseOBJGeometry(content, name, initiallyVisible);
        this.ui.addGeometry(name, 0x000fff, initiallyVisible);
    };
    /**
     * Exports scene to OBJ file format.
     */
    EventDisplay.prototype.exportToOBJ = function () {
        this.graphicsLibrary.exportSceneToOBJ();
        this.infoLogger.add('Exported scene to OBJ');
    };
    /**
     * Parse and load an event from the Phoenix file format (.phnx).
     * @param input Content containing the JSON with event data
     * and other configuration.
     */
    EventDisplay.prototype.parsePhoenixDisplay = function (input) {
        var phoenixScene = JSON.parse(input);
        if (phoenixScene.sceneConfiguration && phoenixScene.scene) {
            // Creating UI folder
            this.ui.addEventDataFolder();
            // Clearing existing event data
            this.graphicsLibrary.clearEventData();
            // Add to scene
            this.loadSceneConfiguration(phoenixScene.sceneConfiguration);
            this.graphicsLibrary.parseGLTFGeometry(phoenixScene.scene);
        }
    };
    /**
     * Exports scene as phoenix format, allowing to load it later and recover the saved configuration.
     */
    EventDisplay.prototype.exportPhoenixDisplay = function () {
        this.graphicsLibrary.exportPhoenixScene();
    };
    /**
     * Parses and loads a geometry in GLTF (.gltf) format.
     * @param input JSON containing the scene as in GLTF (.gltf) format.
     */
    EventDisplay.prototype.parseGLTFGeometry = function (input) {
        var scene = JSON.parse(input);
        this.graphicsLibrary.parseGLTFGeometry(scene);
    };
    /**
     * Loads a GLTF (.gltf) scene/geometry from the given URL.
     * and adds it to the dat.GUI menu.
     * @param url URL to the GLTF (.gltf) file.
     * @param name Name of the loaded scene/geometry.
     * @param scale Scale of the geometry.
     * @param initiallyVisible Whether the geometry is initially visible or not.
     */
    EventDisplay.prototype.loadGLTFGeometry = function (url, name, scale, initiallyVisible) {
        if (initiallyVisible === void 0) { initiallyVisible = true; }
        this.graphicsLibrary.loadGLTFGeometry(url, name, scale, initiallyVisible);
        this.ui.addGeometry(name, 0xff0000, initiallyVisible);
        this.infoLogger.add(name, 'Loaded GLTF geometry');
    };
    /**
     * Loads geometries from JSON.
     * @param json JSON or URL to JSON file of the geometry.
     * @param name Name of the geometry or group of geometries.
     * @param scale Scale of the geometry.
     * @param doubleSided Renders both sides of the material.
     * @param initiallyVisible Whether the geometry is initially visible or not.
     */
    EventDisplay.prototype.loadJSONGeometry = function (json, name, scale, doubleSided, initiallyVisible) {
        if (initiallyVisible === void 0) { initiallyVisible = true; }
        this.graphicsLibrary.loadJSONGeometry(json, name, scale, doubleSided, initiallyVisible);
        this.ui.addGeometry(name, 0xff0000, initiallyVisible);
        this.infoLogger.add(name, 'Loaded JSON geometry');
    };
    /**
     * Load JSON geometry from JSRoot.
     * @param JSROOT JSRoot object containing all the JSROOT functions.
     * @param url URL of the JSRoot geometry file.
     * @param name Name of the geometry.
     * @param scale Scale of the geometry.
     * @param doubleSided Renders both sides of the material.
     * @param initiallyVisible Whether the geometry is initially visible or not.
     */
    EventDisplay.prototype.loadRootJSONGeometry = function (JSROOT, url, name, scale, doubleSided, initiallyVisible) {
        var _this = this;
        if (initiallyVisible === void 0) { initiallyVisible = true; }
        JSROOT.NewHttpRequest(url, 'object', function (obj) {
            _this.loadJSONGeometry(JSROOT.GEO.build(obj, { dflt_colors: true }).toJSON(), name, scale, doubleSided, initiallyVisible);
        }).send();
    };
    /**
     * Load ROOT geometry from JSRoot.
     * @param JSROOT JSRoot object containing all the JSROOT functions.
     * @param url URL of the JSRoot file.
     * @param objectName Name of the object inside the ".root" file.
     * @param name Name of the geometry.
     * @param scale Scale of the geometry.
     * @param doubleSided Renders both sides of the material.
     * @param initiallyVisible Whether the geometry is initially visible or not.
     */
    EventDisplay.prototype.loadRootGeometry = function (JSROOT, url, objectName, name, scale, doubleSided, initiallyVisible) {
        var _this = this;
        if (initiallyVisible === void 0) { initiallyVisible = true; }
        if (url.indexOf('.root') > 0) {
            JSROOT.OpenFile(url, function (file) {
                file.ReadObject(objectName, function (obj) {
                    _this.loadJSONGeometry(JSROOT.GEO.build(obj, { dflt_colors: true }).toJSON(), name, scale, doubleSided, initiallyVisible);
                });
            });
        }
    };
    /**
     * Zoom all the cameras by a specific zoom factor.
     * The factor may either be greater (zoom in) or smaller (zoom out) than 1.
     * @param zoomFactor The factor to zoom by.
     * @param zoomTime The time it takes for a zoom animation to complete.
     */
    EventDisplay.prototype.zoomTo = function (zoomFactor, zoomTime) {
        this.graphicsLibrary.zoomTo(zoomFactor, zoomTime);
    };
    /**
     * Processes event data and geometry for Loading the scene
     * from Phoenix file format (.phnx).
     * @param sceneConfiguration Scene configuration containingevent data and detector geometry.
     */
    EventDisplay.prototype.loadSceneConfiguration = function (sceneConfiguration) {
        for (var _i = 0, _a = Object.keys(sceneConfiguration.eventData); _i < _a.length; _i++) {
            var objectType = _a[_i];
            var typeFolder = this.ui.addEventDataTypeFolder(objectType);
            var collections = sceneConfiguration.eventData[objectType];
            for (var _b = 0, collections_1 = collections; _b < collections_1.length; _b++) {
                var collection = collections_1[_b];
                this.ui.addCollection(typeFolder, collection);
            }
        }
        for (var _c = 0, _d = sceneConfiguration.geometries; _c < _d.length; _c++) {
            var geom = _d[_c];
            this.ui.addGeometry(geom, '#ffffff');
        }
    };
    /**
     * Get all the objects inside a collection.
     * @param collectionName Key of the collection that will be retrieved.
     * @returns Object containing all physics objects from the desired collection.
     */
    EventDisplay.prototype.getCollection = function (collectionName) {
        return this.configuration.getEventDataLoader().getCollection(collectionName);
    };
    /**
     * Get the different collections for the current stored event.
     * @returns List of strings, each representing a collection of the event displayed.
     */
    EventDisplay.prototype.getCollections = function () {
        return this.configuration.getEventDataLoader().getCollections();
    };
    /**
     * Add a callback to onDisplayedEventChange array to call
     * the callback on changes to the displayed event.
     * @param callback Callback to be added to the onDisplayedEventChange array.
     */
    EventDisplay.prototype.listenToDisplayedEventChange = function (callback) {
        this.onDisplayedEventChange.push(callback);
    };
    /**
     * Add a callback to onEventsChange array to call
     * the callback on changes to the events.
     * @param callback Callback to be added to the onEventsChange array.
     */
    EventDisplay.prototype.listenToLoadedEventsChange = function (callback) {
        this.onEventsChange.push(callback);
    };
    /**
     * Get metadata associated to the displayed event (experiment info, time, run, event...).
     * @returns Metadata of the displayed event.
     */
    EventDisplay.prototype.getEventMetadata = function () {
        return this.configuration.getEventDataLoader().getEventMetadata();
    };
    /**
     * Enables calling specified event display methods in console.
     */
    EventDisplay.prototype.enableEventDisplayConsole = function () {
        var _this = this;
        // Defining an EventDisplay object in window to access methods through console
        window.EventDisplay = {
            loadGLTFGeometry: function (sceneUrl, name) {
                _this.loadGLTFGeometry(sceneUrl, name);
            },
            loadOBJGeometry: function (filename, name, colour, doubleSided) {
                _this.loadOBJGeometry(filename, name, colour, doubleSided);
            },
            loadJSONGeometry: function (json, name, scale, doubleSided, initiallyVisible) {
                if (initiallyVisible === void 0) { initiallyVisible = true; }
                _this.loadJSONGeometry(json, name, scale, doubleSided, initiallyVisible);
            }
        };
    };
    /**
     * Sets the renderer to be used to render the event display on the overlayed canvas.
     * @param overlayCanvas An HTML canvas on which the overlay renderer is to be set.
     */
    EventDisplay.prototype.renderOverlay = function (overlayCanvas) {
        this.graphicsLibrary.setOverlayRenderer(overlayCanvas);
    };
    /**
     * Initializes the object which will show information of the selected geometry/event data.
     * @param selectedObject Object to display the data.
     */
    EventDisplay.prototype.allowSelection = function (selectedObject) {
        this.graphicsLibrary.setSelectedObjectDisplay(selectedObject);
    };
    /**
     * Toggles the ability of selecting geometries/event data by clicking on the screen.
     * @param enable Value to enable or disable the functionality.
     */
    EventDisplay.prototype.enableSelecting = function (enable) {
        this.graphicsLibrary.enableSelecting(enable);
    };
    /**
     * Fixes the camera position of the overlay view.
     * @param fixed Whether the overlay view is to be fixed or not.
     */
    EventDisplay.prototype.fixOverlayView = function (fixed) {
        this.graphicsLibrary.fixOverlayView(fixed);
    };
    /**
     * Get the uuid of the currently selected object.
     * @returns uuid of the currently selected object.
     */
    EventDisplay.prototype.getActiveObjectId = function () {
        return this.graphicsLibrary.getActiveObjectId();
    };
    /**
     * Move the camera to look at the object with the given uuid
     * and highlight it.
     * @param uuid uuid of the object.
     */
    EventDisplay.prototype.lookAtObject = function (uuid) {
        this.graphicsLibrary.lookAtObject(uuid);
        this.graphicsLibrary.highlightObject(uuid);
    };
    /**
     * Highlight the object with the given uuid by giving it an outline.
     * @param uuid uuid of the object.
     */
    EventDisplay.prototype.highlightObject = function (uuid) {
        this.graphicsLibrary.highlightObject(uuid);
    };
    /**
     * Enable keyboard controls for the event display.
     */
    EventDisplay.prototype.enableKeyboardControls = function () {
        this.ui.enableKeyboardControls();
        this.graphicsLibrary.enableKeyboardControls();
    };
    /**
     * Animate the camera through the event scene.
     * @param startPos Start position of the translation animation.
     * @param tweenDuration Duration of each tween in the translation animation.
     * @param onAnimationEnd Callback when the last animation ends.
     */
    EventDisplay.prototype.animateThroughEvent = function (startPos, tweenDuration, onAnimationEnd) {
        this.graphicsLibrary
            .animateThroughEvent(startPos, tweenDuration, onAnimationEnd);
    };
    /**
     * Animate the propagation and generation of event data with particle collison.
     * @param tweenDuration Duration of the animation tween.
     * @param onEnd Function to call when all animations have ended.
     */
    EventDisplay.prototype.animateEventWithCollision = function (tweenDuration, onEnd) {
        this.graphicsLibrary.animateEventWithCollision(tweenDuration, onEnd);
    };
    /**
     * Animate the propagation and generation of event data
     * using clipping planes after particle collison.
     * @param tweenDuration Duration of the animation tween.
     * @param onEnd Function to call when all animations have ended.
     */
    EventDisplay.prototype.animateClippingWithCollision = function (tweenDuration, onEnd) {
        this.graphicsLibrary.animateClippingWithCollision(tweenDuration, onEnd);
    };
    return EventDisplay;
}());
export { EventDisplay };
//# sourceMappingURL=event-display.js.map